import React from 'react';
import { observer } from 'mobx-react';

import { MenuItem, Switch, Checkbox, FormControl, CircularProgress } from '@mui/material';
import Box from '@mui/material/Box';

import { Person } from '@mui/icons-material';

import { Formik, Form } from 'formik';

import CheckBoxIcon from '@mui/icons-material/CheckBox';

import CheckBoxOutlineBlankIcon from '@mui/icons-material/CheckBoxOutlineBlank';

import { AxiosError } from 'axios';

import { trackHubSpotEvent } from 'utils/hubspot';

import { CenteredCircularProgress } from 'components/Progress';
import { Button } from 'components/Button';

import { useInfiniteSearch } from 'utils/hooks/infiniteSearch';

import Logger from 'utils/logger';

import { StyledSelect, StyledTextField } from 'components/Input/styles';
import InfiniteAutocomplete from 'components/InfiniteAutocomplete';

import API from 'utils/api';

import { useStore } from 'utils/hooks/store';

import CustomModal from 'components/CustomModal';

import Chip from 'components/Chip';

import { UserRole } from 'core/constants';

import { SearchResponse } from 'utils/api/types';

import {
  StyledModalActions,
  InputLabel,
  ProductLicencesGroup,
  StyledOption,
  RedFormHelperText,
  OptionalText,
} from './styles';
import RoleInfo from './components/RoleInfo';
import { FormValues, getUserValues, getValidationSchema, initialValues } from './types';

const loadingOption = { uuid: 'loading', name: 'Loading...' } as SearchResponse;

const AddUsersModal: React.FC<{ onCloseRefresh: () => void }> = ({ onCloseRefresh }): React.ReactElement => {
  const [openModal, setOpenModal] = React.useState(false);
  const { notification, session } = useStore();
  const { results, searchQuery, loading, loadingMore, hasMore, loadMore } = useInfiniteSearch(API.searchGroups);
  const {
    results: organisationsResults,
    searchQuery: organisationsSearchQuery,
    loading: organisationLoading,
    loadingMore: organisationsLoadingMore,
    hasMore: organisationsHasMore,
    loadMore: loadMoreOrganisations,
  } = useInfiniteSearch(API.organisations.searchOrganisations);
  const [isResetting, setIsResetting] = React.useState(false);

  const onClose = (): void => {
    setOpenModal(false);
    onCloseRefresh();
  };

  const handleSave = async (
    values: FormValues,
    setFieldError: (field: string, message: string | undefined) => void,
    resetForm?: () => void
  ): Promise<void> => {
    if (resetForm) setIsResetting(true);
    const user = getUserValues(values);
    try {
      await API.user.createUser(user)();
      notification.enqueueSuccessSnackbar('User created succesfully.');
      if (session.hasHubSpot) {
        trackHubSpotEvent('User created');
      }

      if (resetForm) {
        resetForm();
      } else {
        onClose();
      }
    } catch (e) {
      const err = e as AxiosError<Error[]>;
      const response = err.response?.data;

      if (Array.isArray(response)) {
        response.forEach((message) => {
          notification.enqueueErrorSnackbar(message as unknown as string);
        });
      } else if (typeof response === 'object') {
        Logger.error('Cannot create user right now.', e);
        notification.enqueueErrorSnackbar('Cannot create user right now.');
        const keys = Object.keys(response);
        if (keys.length === 1 && Array.isArray(response[keys[0]])) {
          const key = keys[0];
          // @ts-ignore
          response[key].forEach((message: string) => {
            setFieldError(key, message);
          });
        }
      }
    }
    setIsResetting(false);
  };

  return (
    <>
      <Button
        variant="contained"
        color="primary"
        onClick={() => setOpenModal(true)}
        size="large"
        sx={{ marginLeft: '8px' }}
      >
        <Person />
        <p style={{ margin: 0, marginLeft: '8px' }}>Add User</p>
      </Button>
      <CustomModal openModal={openModal} modalTitle="Create User" onClose={onClose}>
        <Formik
          initialValues={initialValues}
          onSubmit={(values, { setFieldError }) => handleSave(values, setFieldError)}
          validationSchema={getValidationSchema(session.user?.role as UserRole)}
        >
          {({ values, handleChange, isSubmitting, touched, errors, setFieldValue, resetForm, setFieldError }) => (
            <Form>
              <InputLabel>Name</InputLabel>
              <StyledTextField
                fullWidth
                id="name"
                name="name"
                placeholder="Name"
                value={values.name}
                disabled={isSubmitting}
                onChange={handleChange}
                helperText={touched.name ? errors.name : ''}
                error={(touched.name || false) && errors.name !== undefined}
              />

              <InputLabel>Email</InputLabel>
              <StyledTextField
                fullWidth
                id="email"
                name="email"
                type="email"
                placeholder="example@gmail.com"
                value={values.email}
                disabled={isSubmitting}
                onChange={handleChange}
                helperText={touched.email ? errors.email : ''}
                error={(touched.email || false) && errors.email !== undefined}
              />

              <Box sx={{ display: 'flex', alignItems: 'center' }}>
                <InputLabel>Role</InputLabel>
                <RoleInfo />
              </Box>

              <FormControl sx={{ width: '100%' }}>
                <StyledSelect
                  fullWidth
                  id="role"
                  name="role"
                  variant="outlined"
                  labelId="role-select"
                  value={values.role}
                  disabled={isSubmitting}
                  onChange={handleChange}
                  error={(touched.role || false) && errors.role !== undefined}
                >
                  <MenuItem value="user">User</MenuItem>
                  <MenuItem value="manager">Manager</MenuItem>
                  <MenuItem value="account_owner">Account Owner</MenuItem>
                </StyledSelect>
                {touched.role && errors.role && <RedFormHelperText>{errors.role}</RedFormHelperText>}
              </FormControl>

              <InputLabel>Product licences</InputLabel>
              <ProductLicencesGroup aria-labelledby="checkbox-group">
                <div>
                  <p>Lone Working</p>
                  <Switch
                    size="small"
                    id="productLicenses.alarms"
                    name="productLicenses.alarms"
                    disabled={isSubmitting}
                    onChange={handleChange}
                    value={values.productLicenses.alarms}
                    checked={values.productLicenses.alarms}
                  />
                </div>
                {session.hasEventsLicense && (
                  <div>
                    <p>Events</p>
                    <Switch
                      size="small"
                      id="productLicenses.events"
                      name="productLicenses.events"
                      disabled={isSubmitting}
                      onChange={handleChange}
                      value={values.productLicenses.events}
                      checked={values.productLicenses.events}
                    />
                  </div>
                )}
                <div>
                  <p>Audits</p>
                  <Switch
                    size="small"
                    id="productLicenses.audits"
                    name="productLicenses.audits"
                    disabled={isSubmitting}
                    onChange={handleChange}
                    checked={values.productLicenses.audits}
                    value={values.productLicenses.audits}
                  />
                </div>
                <div>
                  <p>Documents</p>
                  <Switch
                    size="small"
                    id="productLicenses.documents"
                    name="productLicenses.documents"
                    disabled={isSubmitting}
                    onChange={handleChange}
                    checked={values.productLicenses.documents}
                    value={values.productLicenses.documents}
                  />
                </div>
                {session.hasRisksLicense && (
                  <div>
                    <p>Risk Register</p>
                    <Switch
                      size="small"
                      id="productLicenses.risks"
                      name="productLicenses.risks"
                      disabled={isSubmitting}
                      onChange={handleChange}
                      checked={values.productLicenses.risks}
                      value={values.productLicenses.risks}
                    />
                  </div>
                )}
              </ProductLicencesGroup>
              {touched.productLicenses && errors.productLicenses && <div>{errors.productLicenses.alarms}</div>}

              <InputLabel>
                Team{' '}
                <Box component="span" sx={{ fontWeight: 'normal' }}>
                  (Optional)
                </Box>
              </InputLabel>

              <InfiniteAutocomplete
                loadingMore={loadingMore}
                hasMore={hasMore}
                onLoadMore={loadMore}
                multiple
                id="team"
                options={results}
                value={values.teams}
                disableCloseOnSelect
                renderOption={(props, option: SearchResponse, { selected }) =>
                  option.uuid === loadingOption.uuid ? (
                    <StyledOption>
                      <CenteredCircularProgress />
                    </StyledOption>
                  ) : (
                    <StyledOption {...props}>
                      <Checkbox
                        icon={<CheckBoxOutlineBlankIcon fontSize="medium" />}
                        checkedIcon={<CheckBoxIcon fontSize="medium" />}
                        checked={selected}
                      />
                      {option.name}
                    </StyledOption>
                  )
                }
                getOptionLabel={(option: SearchResponse | null) => option?.name as string}
                renderInput={(params) => (
                  <StyledTextField
                    {...params}
                    placeholder={values.teams.length === 0 ? 'Choose option(s)' : ''}
                    onChange={(e) => {
                      searchQuery(e.target.value);
                    }}
                  />
                )}
                renderTags={(tagValue, getTagProps) =>
                  tagValue.map((option, index) => (
                    <Chip {...getTagProps({ index })} label={`${option.name}`} size="medium" fontSize={14} />
                  ))
                }
                loading={loading}
                noOptionsText="-"
                onChange={(_, value) => {
                  setFieldValue('teams', value);
                  searchQuery('');
                }}
                isOptionEqualToValue={(option: SearchResponse | null, val: SearchResponse | null) =>
                  val?.uuid !== undefined && val?.uuid === option?.uuid
                }
                fullWidth
              />

              {session.user?.role === UserRole.Admin && (
                <>
                  <OptionalText>
                    As an Admin, you can select to which organisation all the previously specified users will be
                    invited.
                  </OptionalText>

                  <InfiniteAutocomplete
                    loadingMore={organisationsLoadingMore}
                    hasMore={organisationsHasMore}
                    onLoadMore={loadMoreOrganisations}
                    id="organisation"
                    options={organisationsResults}
                    value={values.organisation}
                    renderOption={(props, option: SearchResponse) =>
                      option.uuid === loadingOption.uuid ? (
                        <StyledOption>
                          <CenteredCircularProgress />
                        </StyledOption>
                      ) : (
                        <StyledOption {...props}>{option.name}</StyledOption>
                      )
                    }
                    getOptionLabel={(option: SearchResponse | null) => option?.name as string}
                    renderInput={(params) => (
                      <StyledTextField
                        {...params}
                        placeholder=""
                        onChange={(e) => {
                          organisationsSearchQuery(e.target.value);
                        }}
                        error={(touched.organisation || false) && errors.organisation !== undefined}
                      />
                    )}
                    loading={organisationLoading}
                    noOptionsText="-"
                    onChange={(_, value) => {
                      setFieldValue('organisation', value);
                      organisationsSearchQuery('');
                    }}
                    isOptionEqualToValue={(option: SearchResponse | null, val: SearchResponse | null) =>
                      val?.uuid !== undefined && val?.uuid === option?.uuid
                    }
                    fullWidth
                  />

                  {touched.role && errors.organisation && (
                    <RedFormHelperText>{errors.organisation.name}</RedFormHelperText>
                  )}
                </>
              )}
              <StyledModalActions>
                <Button
                  type="button"
                  id="cancel-button"
                  onClick={onClose}
                  size="large"
                  disabled={isSubmitting || isResetting}
                >
                  Cancel
                </Button>
                <div>
                  <Button
                    size="large"
                    id="reset-button"
                    variant="outlined"
                    onClick={() => {
                      handleSave(values, setFieldError, resetForm);
                    }}
                    disabled={isSubmitting || isResetting}
                  >
                    {isResetting && <CircularProgress color="inherit" size="16px" sx={{ marginRight: '8px' }} />}
                    Create and Add Another
                  </Button>
                  <Button
                    type="submit"
                    id="add-user-button"
                    variant="contained"
                    size="large"
                    sx={{ marginLeft: '8px' }}
                    disabled={isSubmitting || isResetting}
                  >
                    {isSubmitting && <CircularProgress color="inherit" size="16px" sx={{ marginRight: '8px' }} />}
                    Create
                  </Button>
                </div>
              </StyledModalActions>
            </Form>
          )}
        </Formik>
      </CustomModal>
    </>
  );
};

export default observer(AddUsersModal);
